import {
	createEffect,
	createSignal,
	createUniqueId,
	type JSX,
	mergeProps,
	Show,
} from "solid-js";
import { Counter } from "./Counter";
import "./Toggle.css";
import type { Accessor } from "solid-js";

type ToggleProps = {
	label?: string;
	onChange: (e: Event & { target: HTMLInputElement }) => void;
	labelPosition?: "before" | "after";
	checked: Accessor<boolean>;
	highlightLabelWhenChecked?: boolean;
	showState?: boolean;
	stateLabels?: {
		enabled: string | JSX.Element;
		disabled: string | JSX.Element;
	};
	stateCounts?: { enabled?: number; disabled?: number };
	name?: string;
	id?: string;
	disabled?: boolean;
	"aria-label"?: string;
	theme?: "primary" | "success" | "warning" | "error" | "monochrome" | "ink";
	variant?: "switch" | "button";
	style?: JSX.CSSProperties;
	size?: "medium" | "large";
};

const StateLabel = ({
	checked,
	stateLabels,
}: {
	checked: Accessor<boolean>;
	stateLabels: {
		enabled: string | JSX.Element;
		disabled: string | JSX.Element;
	};
}) => {
	const label = () => (checked() ? stateLabels.enabled : stateLabels.disabled);
	const state = () =>
		checked() === true
			? "gvtui-toggle-state enabled"
			: "gvtui-toggle-state disabled";
	return (
		<span aria-hidden="true" class={state()}>
			{label()}
		</span>
	);
};

/**
 * Switch control for boolean settings with optional state labels.
 *
 * Switch variant: one `role="switch"` control. Button variant: native radios
 * in a fieldset with the same segmented styling.
 *
 * [Design System →](http://localhost:5174/component/Toggle)
 */
export const Toggle = (props: ToggleProps) => {
	const [localChecked, setLocalChecked] = createSignal(props.checked());
	createEffect(() => {
		setLocalChecked(props.checked());
	});
	const stateLabels = mergeProps(
		{
			enabled: "Enabled",
			disabled: "Disabled",
		},
		props.stateLabels,
	);
	const theme = () => props.theme ?? "primary";
	const variant = () => props.variant ?? "switch";
	const labelClass = () =>
		[
			"gvtui-toggle",
			`gvtui-toggle--theme-${theme()}`,
			variant() === "button" ? "gvtui-toggle--variant-button" : "",
			props.labelPosition === "before" ? "gvtui-toggle--label-before" : "",
			props.size === "medium" ? "gvtui-toggle--size-medium" : "",
			props.size === "large" ? "gvtui-toggle--size-large" : "",
			props.highlightLabelWhenChecked
				? "gvtui-toggle--highlight-label-when-checked"
				: "",
		]
			.filter(Boolean)
			.join(" ");
	const defaultName = (props.label ?? "").toLowerCase().replace(/ /g, "-");
	const uid = createUniqueId();
	const controlId =
		props.id ??
		(defaultName.length > 0 ? `toggle-${defaultName}` : `toggle-${uid}`);
	const labelId = `${controlId}-label`;
	const groupLegendId = `${controlId}-legend`;

	const emitChange = (checked: boolean) => {
		setLocalChecked(checked);
		props.onChange({ target: { checked } } as Event & {
			target: HTMLInputElement;
		});
	};

	const segmentAriaLabel = () =>
		props["aria-label"] ??
		(typeof props.label === "string" ? props.label : undefined);

	const switchAccessibleName = () => {
		if (typeof props.label === "string" && props.label.length > 0) {
			return undefined;
		}
		return props["aria-label"];
	};

	const radioName = () => props.name ?? `${controlId}-segment`;
	const offRadioId = `${controlId}-off`;
	const onRadioId = `${controlId}-on`;

	const handleRadioChange = (
		event: Event & { currentTarget: HTMLInputElement },
	) => {
		emitChange(event.currentTarget.value === "on");
	};

	return (
		<div
			class={labelClass()}
			classList={{
				disabled: props.disabled,
				"no-label": !props.label,
				"gvtui-toggle--checked": localChecked(),
			}}
			style={props.style}
		>
			<Show
				when={variant() === "button"}
				fallback={
					<>
						<Show when={props.label}>
							<label id={labelId} for={controlId} class="gvtui-toggle-label">
								{props.label}
							</label>
						</Show>
						<button
							id={controlId}
							name={props.name}
							type="button"
							role="switch"
							class="gvtui-toggle-button-container"
							disabled={props.disabled}
							aria-checked={localChecked()}
							aria-labelledby={props.label ? labelId : undefined}
							aria-label={switchAccessibleName()}
							onClick={() => emitChange(!localChecked())}
						>
							{props.showState && (
								<StateLabel checked={localChecked} stateLabels={stateLabels} />
							)}
							<span class="gvtui-toggle-slider" aria-hidden="true" />
						</button>
					</>
				}
			>
				<Show when={props.label}>
					<span id={labelId} class="gvtui-toggle-label">
						{props.label}
					</span>
				</Show>
				<fieldset
					class="gvtui-toggle-segmented"
					disabled={props.disabled}
					aria-labelledby={
						segmentAriaLabel()
							? groupLegendId
							: props.label
								? labelId
								: undefined
					}
					aria-label={
						!segmentAriaLabel() && !props.label
							? props["aria-label"]
							: undefined
					}
				>
					<Show when={segmentAriaLabel()}>
						<legend id={groupLegendId} class="gvtui-toggle-segmented__legend">
							{segmentAriaLabel()}
						</legend>
					</Show>
					<div class="gvtui-toggle-segmented__slider" aria-hidden="true" />
					<div class="gvtui-toggle-segment-cell">
						<input
							type="radio"
							class="gvtui-toggle-segment-input"
							id={offRadioId}
							name={radioName()}
							value="off"
							checked={!localChecked()}
							disabled={props.disabled}
							onChange={handleRadioChange}
						/>
						<label for={offRadioId} class="gvtui-toggle-segment">
							{stateLabels.disabled}
							<Show when={props.stateCounts?.disabled !== undefined}>
								<Counter
									value={props.stateCounts?.disabled ?? 0}
									size="small"
								/>
							</Show>
						</label>
					</div>
					<div class="gvtui-toggle-segment-cell">
						<input
							type="radio"
							class="gvtui-toggle-segment-input"
							id={onRadioId}
							name={radioName()}
							value="on"
							checked={localChecked()}
							disabled={props.disabled}
							onChange={handleRadioChange}
						/>
						<label for={onRadioId} class="gvtui-toggle-segment">
							{stateLabels.enabled}
							<Show when={props.stateCounts?.enabled !== undefined}>
								<Counter value={props.stateCounts?.enabled ?? 0} size="small" />
							</Show>
						</label>
					</div>
				</fieldset>
			</Show>
		</div>
	);
};
